import { Link } from "wouter";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/page-header";
import { useLanguage } from "@/components/language-provider";
import { ArrowLeft, Building2, Send, CheckCircle2, Users, Globe, Shield, Headphones, Mail, Phone, MapPin } from "lucide-react";
import { WEBSITE_ROUTES, getApiUrl } from "@/lib/config";
import { AnimatedSection } from "@/components/landing/animations";
import { useToast } from "@/hooks/use-toast";

export default function EnterpriseContact() {
  const { language } = useLanguage();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", company: "", phone: "", employees: "", message: "", interest: "dynamics365",
  });

  const t = {
    title: language === "pt" ? "Fale com nossa equipe" : "Talk to our team",
    subtitle: language === "pt"
      ? "Conte-nos sobre suas necessidades e nossa equipe entrará em contato em até 24h."
      : "Tell us about your needs and our team will contact you within 24h.",
    name: language === "pt" ? "Nome completo" : "Full name",
    email: "Email",
    company: language === "pt" ? "Empresa" : "Company",
    phone: language === "pt" ? "Telefone" : "Phone",
    employees: language === "pt" ? "Número de funcionários" : "Number of employees",
    message: language === "pt" ? "Mensagem" : "Message",
    interest: language === "pt" ? "Interesse principal" : "Main interest",
    submit: language === "pt" ? "Enviar mensagem" : "Send message",
    sending: language === "pt" ? "Enviando..." : "Sending...",
    successTitle: language === "pt" ? "Mensagem enviada!" : "Message sent!",
    successMsg: language === "pt" ? "Nossa equipe entrará em contato em breve." : "Our team will contact you soon.",
  };

  const interests = [
    { value: "dynamics365", label: "Dynamics 365" },
    { value: "api", label: "API Integration" },
    { value: "govbr", label: language === "pt" ? "Assinatura GOV.BR" : "GOV.BR Signature" },
    { value: "enterprise", label: language === "pt" ? "Plano Enterprise" : "Enterprise Plan" },
    { value: "other", label: language === "pt" ? "Outro" : "Other" },
  ];

  const benefits = [
    { icon: Users, text: language === "pt" ? "Onboarding dedicado" : "Dedicated onboarding" },
    { icon: Headphones, text: language === "pt" ? "Suporte prioritário" : "Priority support" },
    { icon: Shield, text: language === "pt" ? "SLA garantido" : "Guaranteed SLA" },
    { icon: Globe, text: language === "pt" ? "Deploy personalizado" : "Custom deployment" },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(getApiUrl("/api/contact/enterprise"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        toast({ title: "Error", description: language === "pt" ? "Erro ao enviar. Tente novamente." : "Error sending. Please try again.", variant: "destructive" });
      }
    } catch {
      toast({ title: "Error", description: language === "pt" ? "Erro de conexão." : "Connection error.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full px-4 py-3 rounded-xl border bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 transition-all text-sm";

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: "var(--fs-sage)", color: "#fff" }}>
            <CheckCircle2 className="w-8 h-8" />
          </div>
          <h1 className="text-3xl font-display mb-3">{t.successTitle}</h1>
          <p className="text-muted-foreground mb-8">{t.successMsg}</p>
          <Link href="/"><Button variant="outline" className="rounded-full gap-2"><ArrowLeft className="w-4 h-4" />{language === "pt" ? "Voltar ao início" : "Back to home"}</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <PageHeader />

      <section className="section-padding">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-5 gap-12 max-w-6xl mx-auto">
            {/* Left side - info */}
            <div className="lg:col-span-2">
              <AnimatedSection>
                <h1 className="text-3xl md:text-4xl font-display mb-4">{t.title}</h1>
                <p className="text-muted-foreground leading-relaxed mb-8">{t.subtitle}</p>

                <div className="space-y-4 mb-8">
                  {benefits.map((b, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "hsla(95, 40%, 38%, 0.08)", color: "var(--fs-forest)" }}>
                        <b.icon className="w-4 h-4" />
                      </div>
                      <span className="text-sm font-medium">{b.text}</span>
                    </div>
                  ))}
                </div>

                <div className="separator-botanical" />

                <div className="space-y-3 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2"><Mail className="w-4 h-4" />contato@fillsign.com</div>
                  <div className="flex items-center gap-2"><Phone className="w-4 h-4" />+55 (31) 9999-9999</div>
                  <div className="flex items-center gap-2"><MapPin className="w-4 h-4" />{language === "pt" ? "Belo Horizonte, MG - Brasil" : "Belo Horizonte, MG - Brazil"}</div>
                </div>
              </AnimatedSection>
            </div>

            {/* Right side - form */}
            <div className="lg:col-span-3">
              <AnimatedSection delay={0.2}>
                <form onSubmit={handleSubmit} className="card-organic p-6 sm:p-8 space-y-5">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-1.5 block">{t.name}</label>
                      <input className={inputClass} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1.5 block">{t.email}</label>
                      <input type="email" className={inputClass} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-1.5 block">{t.company}</label>
                      <input className={inputClass} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} required />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1.5 block">{t.phone}</label>
                      <input className={inputClass} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-1.5 block">{t.employees}</label>
                      <select className={inputClass} value={form.employees} onChange={(e) => setForm({ ...form, employees: e.target.value })}>
                        <option value="">--</option>
                        <option value="1-10">1-10</option>
                        <option value="11-50">11-50</option>
                        <option value="51-200">51-200</option>
                        <option value="201-1000">201-1000</option>
                        <option value="1000+">1000+</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1.5 block">{t.interest}</label>
                      <select className={inputClass} value={form.interest} onChange={(e) => setForm({ ...form, interest: e.target.value })}>
                        {interests.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1.5 block">{t.message}</label>
                    <textarea className={`${inputClass} min-h-[120px] resize-none`} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                  </div>
                  <button type="submit" disabled={loading} className="btn-cta flex items-center gap-2 w-full justify-center disabled:opacity-50">
                    <Send className="w-4 h-4" />{loading ? t.sending : t.submit}
                  </button>
                </form>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      <footer className="py-8 border-t text-center text-sm text-muted-foreground">
        <div className="container mx-auto px-4">© {new Date().getFullYear()} FillSign.</div>
      </footer>
    </div>
  );
}
